import { useTheme } from 'foundations-theming/utils';
import { useEffect, useState } from 'react';
import { Route, Switch, generatePath, useHistory, useRouteMatch } from 'react-router-dom';
import { NavMarker } from 'react-rhumb';
import UIButton from 'UIComponents/button/UIButton';
import UIPanelBody from 'UIComponents/panel/UIPanelBody';
import UIPanelSection from 'UIComponents/panel/UIPanelSection';
import UIResultsMessage from 'UIComponents/results/UIResultsMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import Trackables from 'automation-ui-usage-tracking/Trackables';
import { useTrackEvent } from 'automation-ui-usage-tracking/usageTrackingHooks';
import EnrollmentActionConfigHeader from 'automation-ui-enrollment/components/common/EnrollmentActionConfigHeader';
import TriggerTypeButtonList from 'automation-ui-enrollment/components/eventEnrollment/TriggerTypeButtonList';
import { TriggerTypeToRoute } from 'automation-ui-enrollment/constants/TriggerTypeRoutes';
import Routes from 'automation-ui-enrollment/constants/Routes';
import UISection from 'UIComponents/section/UISection';
import FormattedMessageWithObjectTypeName from 'automation-ui-i18n/FormattedMessageWithObjectTypeName';
import EnrollmentSaveButton from 'automation-ui-enrollment/components/common/EnrollmentSaveButton';
import UIAlert from 'UIComponents/alert/UIAlert';
import ReenrollmentSelect from 'automation-ui-enrollment/components/ReenrollmentSelect';
import TriggersAIAssistantButton from 'automation-ui-enrollment/components/eventEnrollment/TriggersAIAssistantButton';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const SelectTriggerTypeContainer = ({
  flow,
  onClose,
  readOnly,
  settingsComponent,
  unenrollmentComponent,
  shouldReenroll,
  setShouldReenroll,
  updateEnrollmentCriteria
}) => {
  const trellisTheme = useTheme();
  const history = useHistory();
  const trackEvent = useTrackEvent();
  const {
    params: {
      revisionId
    }
  } = useRouteMatch();
  const redirectToEventTriggers = flow.enrollmentTrigger === 'EVENT' && (flow.flowEventFilters.length !== 0 || flow.associatedLists.find(list => list.listTypes.includes('ADDED_TO_LIST_TRIGGER') || list.listTypes.includes('REMOVED_FROM_LIST_TRIGGER')));
  const redirectToListTriggers = flow.enrollmentTrigger === 'LIST' && flow.associatedLists.find(list => list.listTypes.includes('ENROLLMENT_LIST'));
  useEffect(() => {
    // track the user opening the trigger selection page
    trackEvent(Trackables.OPENED_TRIGGER_SELECTION_PANEL);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    // Reroute if we refresh on the 'edit/triggers' screen but triggers are already set up
    if (redirectToEventTriggers) {
      history.push('triggers/event');
    }
    if (redirectToListTriggers) {
      history.push('actions/enrollment/filters');
    }
  }, [redirectToEventTriggers, redirectToListTriggers, history]);
  const isPreviewWithNoTriggers = readOnly && flow.enrollmentTrigger === 'NEW';
  const handleChange = triggerTypeOption => {
    if (triggerTypeOption === 'DATASET') {
      trackEvent(Trackables.CLICKED_ADD_DATASET_TRIGGER, {
        assetType: 'dataset'
      });
    }
    history.replace(TriggerTypeToRoute[triggerTypeOption]);
  };
  const [saveStatus, setSaveStatus] = useState('UNINITIALIZED');
  return /*#__PURE__*/_jsx(NavMarker, {
    name: "TRIGGER_SELECTION_PANEL",
    children: /*#__PURE__*/_jsxs(Route, {
      path: [Routes.FLOW_CANVAS_ENROLLMENT_CONFIG, Routes.FLOW_CANVAS_REVISION_ENROLLMENT_CONFIG],
      children: [/*#__PURE__*/_jsx(EnrollmentActionConfigHeader, {
        header: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "automation-ui-enrollment.enrollmentHeader"
        }),
        enrollmentPath: revisionId && !isNaN(parseInt(revisionId, 10)) ? generatePath(Routes.FLOW_CANVAS_REVISION_ENROLLMENT_CONFIG, {
          screen: 'edit',
          flowId: flow.flowId,
          revisionId
        }) : generatePath(Routes.FLOW_CANVAS_ENROLLMENT_CONFIG, {
          screen: 'edit',
          flowId: flow.flowId
        }),
        unenrollmentPath: revisionId && !isNaN(parseInt(revisionId, 10)) ? generatePath(Routes.FLOW_CANVAS_REVISION_UNENROLLMENT_CONFIG, {
          screen: 'edit',
          flowId: flow.flowId,
          revisionId
        }) : generatePath(Routes.FLOW_CANVAS_UNENROLLMENT_CONFIG, {
          screen: 'edit',
          flowId: flow.flowId
        }),
        reenrollmentPath: revisionId && !isNaN(parseInt(revisionId, 10)) ? generatePath(Routes.FLOW_CANVAS_REVISION_REENROLLMENT_CONFIG, {
          screen: 'edit',
          flowId: flow.flowId,
          revisionId
        }) : generatePath(Routes.FLOW_CANVAS_REENROLLMENT_CONFIG, {
          screen: 'edit',
          flowId: flow.flowId
        }),
        children: /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(UIButton, {
            onClick: onClose,
            responsive: false,
            use: "tertiary-light",
            size: "small",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "automation-ui-enrollment.redesign.cancelButton"
            })
          }), /*#__PURE__*/_jsx(EnrollmentSaveButton, {
            use: "tertiary",
            size: "small",
            saveFailed: saveStatus === 'FAILED',
            savePending: saveStatus === 'STARTED',
            onSave: () => {
              setSaveStatus('STARTED');
              updateEnrollmentCriteria({}, () => {
                setSaveStatus('SUCCEEDED');
                onClose();
              }, () => setSaveStatus('FAILED')).catch(() => {});
            },
            allowSave: !readOnly
          })]
        })
      }), /*#__PURE__*/_jsxs(Switch, {
        children: [/*#__PURE__*/_jsx(Route, {
          exact: true,
          path: [Routes.FLOW_CANVAS_ENROLLMENT_CONFIG, Routes.FLOW_CANVAS_REVISION_ENROLLMENT_CONFIG],
          children: /*#__PURE__*/_jsxs(_Fragment, {
            children: [!readOnly && !isPreviewWithNoTriggers && /*#__PURE__*/_jsx(TriggersAIAssistantButton, {
              use: "banner"
            }), /*#__PURE__*/_jsxs(UIPanelBody, {
              "data-test-id": "trigger-selection-panel",
              style: {
                background: settingsComponent ? trellisTheme.color['do-not-use-gypsum'] : trellisTheme.color['do-not-use-olaf']
              },
              children: [isPreviewWithNoTriggers ? /*#__PURE__*/_jsx(UIPanelSection, {
                className: "m-bottom-0 p-bottom-8",
                style: {
                  background: trellisTheme.color['do-not-use-olaf']
                },
                children: /*#__PURE__*/_jsx(UIResultsMessage, {
                  illustration: "empty-state-charts",
                  illustrationProps: {
                    width: 187
                  },
                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "automation-ui-enrollment.config.filters.empty-state"
                  })
                })
              }) : /*#__PURE__*/_jsxs(UIPanelSection, {
                className: "m-bottom-0 p-bottom-8",
                style: {
                  background: trellisTheme.color['do-not-use-olaf']
                },
                children: [/*#__PURE__*/_jsx(UIAlert, {
                  use: "inline",
                  className: "m-bottom-5",
                  children: /*#__PURE__*/_jsx(FormattedMessageWithObjectTypeName, {
                    baseLangKey: "automation-ui-enrollment.redesign.enrollSelection.manualEnrollAlert_cob",
                    objectTypeId: flow.objectTypeId
                  })
                }), /*#__PURE__*/_jsx(TriggerTypeButtonList, {
                  objectTypeId: flow.objectTypeId,
                  onChange: handleChange,
                  readOnly: readOnly
                })]
              }), settingsComponent]
            })]
          })
        }), /*#__PURE__*/_jsx(Route, {
          exact: true,
          path: [Routes.FLOW_CANVAS_UNENROLLMENT_CONFIG, Routes.FLOW_CANVAS_REVISION_UNENROLLMENT_CONFIG],
          children: /*#__PURE__*/_jsx(UIPanelBody, {
            children: /*#__PURE__*/_jsx(UIPanelSection, {
              children: unenrollmentComponent
            })
          })
        }), /*#__PURE__*/_jsx(Route, {
          exact: true,
          path: [Routes.FLOW_CANVAS_REENROLLMENT_CONFIG, Routes.FLOW_CANVAS_REVISION_REENROLLMENT_CONFIG],
          children: /*#__PURE__*/_jsx(UIPanelBody, {
            children: /*#__PURE__*/_jsx(UIPanelSection, {
              children: /*#__PURE__*/_jsx(UISection, {
                className: "m-bottom-5",
                children: /*#__PURE__*/_jsx(ReenrollmentSelect, {
                  shouldReenroll: !!shouldReenroll,
                  setShouldReenroll: checked => {
                    setShouldReenroll(checked);
                  },
                  readOnly: readOnly,
                  objectTypeId: flow.objectTypeId
                })
              })
            })
          })
        })]
      })]
    })
  });
};
export default SelectTriggerTypeContainer;